import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Loader2, RotateCcw } from 'lucide-react'
import { clock, dateTime } from '../lib/format'

/**
 * Round-trip playback picker: Dhar -> Jhulwaniya -> Dhule -> Jhulwaniya -> Dhar, as
 * serving/round_trip_playback.py defines it. A round trip starts when the truck leaves
 * Dhar and ends when it is back in Dhar; until then it is LIVE and plays up to now. A
 * truck that comes back to Dhar without reaching Dhule is not a round trip -- it is
 * listed under its own category, "Returned before Dhule".
 *
 * One fetch per window/refresh -- no polling (CLAUDE.md live-data rule).
 */

export type CycleCategory = 'round_trip' | 'short_return'
export type CycleStatus = 'complete' | 'live' | 'unclosed'
export type CyclePhase = 'outbound' | 'at_dhule' | 'returning' | 'back_at_dhar'

export interface PlaybackCycle {
  cycle_id: string
  vehicle_id: string
  category: CycleCategory
  status: CycleStatus
  phase: CyclePhase
  started_ts: number
  ended_ts: number | null
  duration_sec: number
  milestones: {
    left_dhar: number
    jhulwaniya_out: number | null
    reached_dhule: number | null
    left_dhule: number | null
    jhulwaniya_back: number | null
    back_at_dhar: number | null
  }
  last_seen_ts: number | null
  /** Arrivals at Jhulwaniya / Dhule / back at Dhar inside the trip. */
  visits: number
  /** Boundaries come from the trucks' GPS fixes against the fence polygons. */
  boundary_source?: 'gps'
  /** Longest GPS silence in the trip that could hide a Dhar/Dhule visit (seconds). */
  gps_max_gap_sec?: number
  /** True when that silence is over 3 h: the category may be a data gap, not the road. */
  gps_gap_warning?: boolean
  /** Replay only: GPS fixes in the trip window. */
  gps_points?: number
}

export const CATEGORY_LABEL: Record<CycleCategory, string> = {
  round_trip: 'Round trip',
  short_return: 'Returned before Dhule',
}

export const PHASE_LABEL: Record<CyclePhase, string> = {
  outbound: 'heading to Dhule',
  at_dhule: 'at Dhule',
  returning: 'returning to Dhar',
  back_at_dhar: 'back at Dhar',
}

export function cycleStatusLabel(c: Pick<PlaybackCycle, 'status' | 'phase'>): string {
  if (c.status === 'live') return `Live · ${PHASE_LABEL[c.phase]}`
  if (c.status === 'unclosed') return 'No return recorded'
  return 'Complete'
}

export function fmtDuration(sec: number): string {
  const h = Math.floor(sec / 3600)
  const m = Math.floor((sec % 3600) / 60)
  if (h >= 24) return `${Math.floor(h / 24)}d ${h % 24}h`
  return h > 0 ? `${h}h ${m}m` : `${m}m`
}

type CategoryFilter = 'all' | CycleCategory | 'live'
const FILTERS: { id: CategoryFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'round_trip', label: 'Round trips' },
  { id: 'short_return', label: 'Returned before Dhule' },
  { id: 'live', label: 'Live' },
]
const DAYS = [1, 3, 7, 14] as const
type Range = `${(typeof DAYS)[number]}` | 'custom'
const IST_MS = 330 * 60_000
const DAY_MS = 86_400_000

/** YYYY-MM-DD of the IST calendar day `ms` falls on. */
export function istDate(ms: number): string {
  return new Date(ms + IST_MS).toISOString().slice(0, 10)
}

/** The list URL for a range: last N days, or an inclusive IST date range. */
export function cyclesUrl(range: Range, from: string, to: string): string {
  if (range !== 'custom') return `/api/round-trips/playback?days=${range}`
  const end = istDate(Date.parse(`${to}T00:00:00+05:30`) + DAY_MS)  // inclusive "to" day
  const q = new URLSearchParams({ from: `${from}T00:00:00+05:30`, to: `${end}T00:00:00+05:30` })
  return `/api/round-trips/playback?${q.toString()}`
}

export function filterCycles(cycles: readonly PlaybackCycle[], filter: CategoryFilter, query: string): PlaybackCycle[] {
  const q = query.trim().toUpperCase()
  return cycles.filter((c) => {
    if (q && !c.vehicle_id.toUpperCase().includes(q)) return false
    if (filter === 'live') return c.status === 'live'
    return filter === 'all' || c.category === filter
  })
}

const STOPS: { key: keyof PlaybackCycle['milestones']; label: string }[] = [
  { key: 'left_dhar', label: 'Dhar' },
  { key: 'jhulwaniya_out', label: 'Jhul' },
  { key: 'reached_dhule', label: 'Dhule' },
  { key: 'jhulwaniya_back', label: 'Jhul' },
  { key: 'back_at_dhar', label: 'Dhar' },
]

/** Dhar · Jhul · Dhule · Jhul · Dhar with the time each was reached; unreached stops are
 *  hollow, so "where is it now" reads at a glance. */
export function CycleMilestones({ c }: { c: PlaybackCycle }): JSX.Element {
  const stops = c.category === 'short_return' ? STOPS.filter((s) => s.key !== 'reached_dhule' && s.key !== 'jhulwaniya_back') : STOPS
  return (
    <ol className="mt-1.5 flex items-start" aria-label="Milestones">
      {stops.map((s, i) => {
        const ts = c.milestones[s.key]
        const done = ts != null
        return (
          <li key={`${s.key}-${i}`} className="flex min-w-0 flex-1 flex-col items-center text-center">
            <div className="flex w-full items-center">
              <span className={`h-px flex-1 ${i === 0 ? 'opacity-0' : done ? 'bg-[#2563EB]' : 'bg-[#CBD5E1]'}`} />
              <span
                className={`h-2 w-2 shrink-0 rounded-full border ${done ? 'border-[#2563EB] bg-[#2563EB]' : 'border-[#94A3B8] bg-white'}`}
                title={done ? `${s.label} ${dateTime(ts)}` : `${s.label} not reached`}
              />
              <span className={`h-px flex-1 ${i === stops.length - 1 ? 'opacity-0' : stops[i + 1] && c.milestones[stops[i + 1]!.key] != null ? 'bg-[#2563EB]' : 'bg-[#CBD5E1]'}`} />
            </div>
            <span className={`mt-0.5 text-[9.5px] font-medium ${done ? 'text-[#334155]' : 'text-[#94A3B8]'}`}>{s.label}</span>
            <span className="font-mono text-[9px] text-[#94A3B8]" title={done ? dateTime(ts) : undefined}>{done ? clock(ts).slice(0, 5) : '—'}</span>
          </li>
        )
      })}
    </ol>
  )
}

export function RoundTripCyclePicker({
  onPick, activeId,
}: { onPick: (c: PlaybackCycle) => void; activeId?: string | null }): JSX.Element {
  const [range, setRange] = useState<Range>('3')
  const [customFrom, setCustomFrom] = useState(() => istDate(Date.now() - 6 * DAY_MS))
  const [customTo, setCustomTo] = useState(() => istDate(Date.now()))
  const url = cyclesUrl(range, customFrom, customTo)
  const [filter, setFilter] = useState<CategoryFilter>('all')
  const [query, setQuery] = useState('')
  const [cycles, setCycles] = useState<PlaybackCycle[]>([])
  const [err, setErr] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [nonce, setNonce] = useState(0)

  const refresh = useCallback(() => setNonce((n) => n + 1), [])

  useEffect(() => {
    const ctl = new AbortController()
    setLoading(true)
    fetch(url, { signal: ctl.signal })
      .then(async (r) => {
        const body = await r.json() as { cycles?: PlaybackCycle[]; error?: string; detail?: string }
        if (!r.ok || body.error) throw new Error(body.error ?? body.detail ?? r.statusText)
        setCycles(body.cycles ?? [])
        setErr(null)
      })
      .catch((e: unknown) => {
        if (ctl.signal.aborted) return
        setErr(e instanceof Error ? e.message : String(e))
      })
      .finally(() => { if (!ctl.signal.aborted) setLoading(false) })
    return () => ctl.abort()
  }, [url, nonce])

  const shown = useMemo(() => filterCycles(cycles, filter, query), [cycles, filter, query])
  // The Command Centre re-renders on every live SSE message. Without this the whole list
  // (dozens of cards, several Intl date formats each) re-rendered with it and starved the
  // main thread -- the page froze as soon as a few days of trips were listed. The rendered
  // list is memoised on its real inputs; the latest onPick is read through a ref so a new
  // inline callback from the parent does not invalidate it.
  const onPickRef = useRef(onPick)
  onPickRef.current = onPick
  const list = useMemo(() => (
    <ul className="min-h-0 flex-1 space-y-1.5 overflow-auto pr-0.5" aria-label="Round trips">
      {shown.map((c) => (
        <li key={c.cycle_id}>
          <CycleCard c={c} active={activeId === c.cycle_id} onPick={(x) => onPickRef.current(x)} />
        </li>
      ))}
    </ul>
  ), [shown, activeId])
  const counts = useMemo(() => ({
    all: cycles.length,
    round_trip: cycles.filter((c) => c.category === 'round_trip').length,
    short_return: cycles.filter((c) => c.category === 'short_return').length,
    live: cycles.filter((c) => c.status === 'live').length,
  }), [cycles])

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2">
      <div className="flex shrink-0 items-center gap-1.5">
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Vehicle…"
          aria-label="Filter by vehicle"
          className="h-7 min-w-0 flex-1 rounded-lg border border-[rgba(15,23,42,0.12)] bg-white px-2 text-[12px] outline-none focus:border-[rgba(37,99,235,0.5)]"
        />
        <select
          value={range}
          onChange={(e) => setRange(e.target.value as Range)}
          aria-label="Started within"
          className="h-7 rounded-lg border border-[rgba(15,23,42,0.12)] bg-white px-1.5 text-[11.5px]"
        >
          {DAYS.map((d) => <option key={d} value={String(d)}>{d === 1 ? 'Last 24 h' : `Last ${d} days`}</option>)}
          <option value="custom">Pick dates…</option>
        </select>
        <button
          type="button"
          onClick={refresh}
          aria-label="Refresh"
          title="Refresh (live trips move on)"
          className="flex h-7 w-7 items-center justify-center rounded-lg border border-[rgba(15,23,42,0.12)] bg-white text-[#475569] hover:text-[#1D4ED8]"
        >
          <RotateCcw size={12} />
        </button>
      </div>
      {range === 'custom' && (
        <div className="flex shrink-0 items-center gap-1.5 text-[11px] text-[#64748B]">
          <span>Left Dhar</span>
          <input
            type="date"
            value={customFrom}
            max={customTo}
            onChange={(e) => e.target.value && setCustomFrom(e.target.value)}
            aria-label="From date"
            className="h-7 min-w-0 flex-1 rounded-lg border border-[rgba(15,23,42,0.12)] bg-white px-1.5 text-[11.5px] text-[#0F172A]"
          />
          <span>to</span>
          <input
            type="date"
            value={customTo}
            min={customFrom}
            onChange={(e) => e.target.value && setCustomTo(e.target.value)}
            aria-label="To date"
            className="h-7 min-w-0 flex-1 rounded-lg border border-[rgba(15,23,42,0.12)] bg-white px-1.5 text-[11.5px] text-[#0F172A]"
          />
        </div>
      )}
      <div className="flex shrink-0 flex-wrap gap-1" role="group" aria-label="Category">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            aria-pressed={filter === f.id}
            onClick={() => setFilter(f.id)}
            className={`rounded-full border px-2 py-0.5 text-[10.5px] transition-colors ${
              filter === f.id
                ? 'border-[rgba(37,99,235,0.45)] bg-[#EFF6FF] text-[#1D4ED8]'
                : 'border-[rgba(15,23,42,0.10)] bg-white text-[#64748B] hover:text-[#0F172A]'
            }`}
          >
            {f.label} <span className="tabular-nums opacity-70">{counts[f.id]}</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 px-2 py-4 text-[12px] text-[#64748B]">
          <Loader2 size={14} className="animate-spin" /> Loading round trips…
        </div>
      ) : err ? (
        <p className="px-2 py-3 text-[12px] text-[#B91C1C]">{err}</p>
      ) : !shown.length ? (
        <p className="px-2 py-3 text-[12px] text-[#64748B]">
          {cycles.length
            ? 'Nothing matches these filters.'
            : 'No truck left Dhar in this window. Try a longer range or “Pick dates…”.'}
        </p>
      ) : list}
    </div>
  )
}

function CycleCard({ c, active, onPick }: { c: PlaybackCycle; active: boolean; onPick: (c: PlaybackCycle) => void }): JSX.Element {
  return (
    <button
      type="button"
      onClick={() => onPick(c)}
      aria-current={active ? 'true' : undefined}
      className={`w-full rounded-[10px] border px-2.5 py-2 text-left transition-colors ${
        active
          ? 'border-[rgba(37,99,235,0.35)] bg-[#EFF6FF]'
          : 'border-[rgba(15,23,42,0.07)] bg-white hover:bg-[#F8FAFC]'
      }`}
    >
      <div className="flex items-baseline gap-2">
        <span className="font-mono text-[12px] font-semibold text-[#0F172A]">{c.vehicle_id}</span>
        <span className={`whitespace-nowrap rounded-full px-1.5 py-px text-[9.5px] font-semibold ${
          c.category === 'short_return' ? 'bg-[#FFFBEB] text-[#92400E]' : 'bg-[#EFF6FF] text-[#1D4ED8]'
        }`}
        >
          {CATEGORY_LABEL[c.category]}
        </span>
        <span className={`ml-auto flex shrink-0 items-center gap-1 whitespace-nowrap text-[10px] ${c.status === 'live' ? 'font-semibold text-[#047857]' : 'text-[#64748B]'}`}>
          {c.status === 'live' && <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#10B981]" />}
          {cycleStatusLabel(c)}
        </span>
      </div>
      <div className="mt-0.5 flex flex-wrap gap-x-2 text-[10px] text-[#94A3B8]">
        <span>left Dhar {dateTime(c.started_ts)}</span>
        <span>{fmtDuration(c.duration_sec)}{c.status === 'live' ? ' so far' : ''}</span>
        {c.gps_gap_warning ? (
          <span
            className="rounded-full bg-[#FFFBEB] px-1.5 text-[9.5px] font-semibold text-[#92400E] ring-1 ring-inset ring-[rgba(217,119,6,0.3)]"
            title="No GPS for this long, and a Dhar/Dhule visit could fit inside it -- the category may be a data gap"
          >
            GPS gap {fmtDuration(c.gps_max_gap_sec ?? 0)}
          </span>
        ) : null}
      </div>
      <CycleMilestones c={c} />
    </button>
  )
}
